import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const ReferralDashboard = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://builtfuturelife.com/api';
  const token = localStorage.getItem('token');
  
  const [dashboardData, setDashboardData] = useState(null);
  const [referredUsers, setReferredUsers] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [userBalance, setUserBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');
  const [planProgress, setPlanProgress] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    
    const loadInitialData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboardData(),
        fetchUserBalance()
      ]);
    };
    
    loadInitialData();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      setError(null);
      
      const response = await axios.get(`${API_BASE_URL}/referral/dashboard`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        },
        timeout: 10000
      });
      
      if (response.data.success) {
        setDashboardData(response.data);
        
        if (response.data.planProgress) {
          const processedPlans = processPlanProgress(response.data.planProgress);
          setPlanProgress(processedPlans);
        }
        
        await Promise.all([
          fetchReferredUsers(),
          fetchEarnings()
        ]);
      } else {
        throw new Error(response.data.message || 'Failed to load dashboard');
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        localStorage.removeItem('token');
        setTimeout(() => window.location.href = '/login', 2000);
      } else if (err.code === 'ECONNABORTED') {
        setError('Request timeout. Please check your connection.');
      } else {
        setError(err.response?.data?.message || 'Failed to load referral dashboard');
      }
      
      setDashboardData({
        referralLink: 'Not available',
        referralCode: 'Not available',
        stats: {
          totalReferrals: 0,
          totalEarned: 0,
          awardsGiven: 0
        },
        planProgress: {}
      });
      setPlanProgress([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const processPlanProgress = useCallback((planProgressData) => {
    if (!planProgressData || typeof planProgressData !== 'object') return [];
    
    const plans = Object.values(planProgressData);
    
    return plans.map(plan => {
      const currentReferrals = Number(plan.currentReferrals) || 0;
      const neededReferrals = Number(plan.neededReferrals) || 5;
      const progressValue = Number(plan.progress) || 0;
      const calculatedProgress = Math.min(100, (currentReferrals / neededReferrals) * 100);
      
      return {
        ...plan,
        planId: plan.planId || 'unknown',
        planName: plan.planName || 'Unknown Plan',
        planPrice: Number(plan.planPrice) || 0,
        awardAmount: Number(plan.awardAmount) || 0,
        currentReferrals: currentReferrals,
        neededReferrals: neededReferrals,
        progress: progressValue || calculatedProgress,
        hasEarnedAward: plan.hasEarnedAward || currentReferrals >= neededReferrals,
        canEarn: plan.canEarn !== undefined ? plan.canEarn : currentReferrals < neededReferrals,
        referralsNeeded: Math.max(0, neededReferrals - currentReferrals)
      };
    });
  }, []);

  const fetchUserBalance = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/balance`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
      if (response.data.balance !== undefined) setUserBalance(response.data.balance);
    } catch (err) {
      console.error('Balance fetch error:', err);
    }
  }, [token]);

  const fetchReferredUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/referral/users`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
      if (response.data.success) setReferredUsers(response.data.referrals || []);
    } catch (err) {
      console.error('Users fetch error:', err);
    }
  }, [token]);

  const fetchEarnings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/referral/earnings`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
      if (response.data.success) setEarnings(response.data.earnings || []);
    } catch (err) {
      console.error('Earnings fetch error:', err);
    }
  }, [token]);

  const handleCopyLink = useCallback(async () => {
    if (dashboardData?.referralLink && dashboardData.referralLink !== 'Not available') {
      try {
        await navigator.clipboard.writeText(dashboardData.referralLink);
        setInfoMessage('Referral link copied to clipboard!');
        setTimeout(() => setInfoMessage(''), 3000);
      } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = dashboardData.referralLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setInfoMessage('Link copied!');
        setTimeout(() => setInfoMessage(''), 3000);
      }
    }
  }, [dashboardData]);

  const handleCopyCode = useCallback(() => {
    if (dashboardData?.referralCode && dashboardData.referralCode !== 'Not available') {
      navigator.clipboard.writeText(dashboardData.referralCode);
      setInfoMessage('Referral code copied!');
      setTimeout(() => setInfoMessage(''), 3000);
    }
  }, [dashboardData]);

  const forceRefreshAll = useCallback(() => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    setLoading(true);
    setError(null);
    setDashboardData(null);
    setReferredUsers([]);
    setEarnings([]);
    setPlanProgress([]);
    
    Promise.all([
      fetchDashboardData(),
      fetchUserBalance()
    ]).finally(() => {
      setIsRefreshing(false);
    });
  }, [fetchDashboardData, fetchUserBalance, isRefreshing]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'Invalid Date';
    }
  }, []);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  }, []);

  const calculateTotalEarnings = useCallback(() => {
    if (!earnings.length) return dashboardData?.stats?.totalEarned || 0;
    return earnings.reduce((total, earning) => total + (earning.amount || 0), 0);
  }, [earnings, dashboardData]);

  const statsData = useMemo(() => ({
    totalReferrals: dashboardData?.stats?.totalReferrals || 0,
    totalEarned: dashboardData?.stats?.totalEarned || 0,
    awardsGiven: dashboardData?.stats?.awardsGiven || 0,
    referralLink: dashboardData?.referralLink || 'Not available',
    referralCode: dashboardData?.referralCode || 'Not available'
  }), [dashboardData]);

  const ProgressBar = useCallback(({ progress, current, total }) => {
    const width = Math.min(100, Math.max(0, progress));
    return (
      <div style={{ margin: '12px 0' }}>
        <div style={{
          width: '100%',
          height: '8px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${width}%`,
            height: '100%',
            background: width >= 100 ? '#00d4ff' : '#00d4ff',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '12px',
          color: '#94a3b8',
          marginTop: '4px'
        }}>
          <span>{current}/{total} referrals</span>
          <span>{width.toFixed(1)}% complete</span>
        </div>
      </div>
    );
  }, []);

  if (loading && !dashboardData) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        gap: '20px',
        backgroundColor: '#0a0a0a'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(0, 212, 255, 0.2)',
          borderTop: '4px solid #00d4ff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: '#94a3b8' }}>Loading referral dashboard...</p>
        <button onClick={forceRefreshAll} disabled={isRefreshing} style={{
          padding: '10px 20px',
          background: '#1a1a2e',
          border: '1px solid rgba(0, 212, 255, 0.3)',
          borderRadius: '10px',
          color: '#00d4ff',
          cursor: isRefreshing ? 'not-allowed' : 'pointer',
          opacity: isRefreshing ? 0.5 : 1
        }}>
          {isRefreshing ? 'Refreshing...' : 'Force Refresh'}
        </button>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const totalEarnings = calculateTotalEarnings();

  return (
    <div style={{
      padding: isMobile ? '16px' : '20px',
      maxWidth: '1400px',
      margin: '0 auto',
      paddingBottom: isMobile ? '80px' : '20px',
      backgroundColor: '#0a0a0a',
      minHeight: '100vh'
    }}>
      {/* Header - Dark Theme */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
        borderRadius: '24px',
        padding: '32px 24px',
        marginBottom: '24px',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid rgba(0, 212, 255, 0.3)',
        boxShadow: '0 10px 30px rgba(0, 212, 255, 0.2)'
      }}>
        <div style={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.1), transparent)',
          borderRadius: '50%'
        }} />
        <h1 style={{
          fontSize: isMobile ? '28px' : '36px',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #fff, #00d4ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '40px' }}>👥</span>
          Referral Program
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '16px', margin: 0 }}>
          Refer 5 friends who purchase the same plan, earn the FULL PLAN PRICE!
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          marginBottom: '20px',
          padding: '12px 16px',
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f87171' }}>
            <span>⚠️</span>
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '20px' }}>×</button>
        </div>
      )}

      {infoMessage && (
        <div style={{
          marginBottom: '20px',
          padding: '12px 16px',
          background: 'rgba(0, 212, 255, 0.15)',
          border: '1px solid rgba(0, 212, 255, 0.3)',
          borderRadius: '12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#00d4ff' }}>
            <span>✅</span>
            <span>{infoMessage}</span>
          </div>
          <button onClick={() => setInfoMessage('')} style={{ background: 'none', border: 'none', color: '#00d4ff', cursor: 'pointer', fontSize: '20px' }}>×</button>
        </div>
      )}

      {/* Main Content */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1.5fr',
        gap: '24px'
      }}>
        {/* Left Column - Dark Theme */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Referral Link Card */}
          <div style={{
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            padding: '24px'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#00d4ff', marginBottom: '20px' }}>Your Referral Link</h2>
            
            <div style={{
              background: '#0f0f0f',
              borderRadius: '16px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <code style={{
                  flex: 1,
                  background: 'rgba(0, 212, 255, 0.05)',
                  padding: '12px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  color: '#94a3b8',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace'
                }}>
                  {statsData.referralLink}
                </code>
                <button
                  onClick={handleCopyLink}
                  disabled={statsData.referralLink === 'Not available'}
                  style={{
                    padding: '12px 24px',
                    background: '#00d4ff',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#1a1a2e',
                    cursor: 'pointer',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}
                >
                  📋 Copy Link
                </button>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(0, 212, 255, 0.2)'
              }}>
                <span style={{ color: '#94a3b8', fontSize: '14px' }}>Your Code:</span>
                <code style={{
                  fontFamily: 'monospace',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#00d4ff',
                  background: 'rgba(0, 212, 255, 0.1)',
                  padding: '6px 12px',
                  borderRadius: '8px'
                }}>
                  {statsData.referralCode}
                </code>
                <button
                  onClick={handleCopyCode}
                  disabled={statsData.referralCode === 'Not available'}
                  style={{
                    padding: '8px 16px',
                    background: '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '8px',
                    color: '#00d4ff',
                    cursor: 'pointer'
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats - Dark Theme */}
          <div style={{
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            padding: '24px'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#00d4ff', marginBottom: '20px' }}>Quick Overview</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                background: '#0f0f0f',
                borderRadius: '16px'
              }}>
                <div style={{ fontSize: '32px' }}>👥</div>
                <div>
                  <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#00d4ff', margin: 0 }}>{statsData.totalReferrals}</h3>
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>Total Referrals</p>
                </div>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                background: '#0f0f0f',
                borderRadius: '16px'
              }}>
                <div style={{ fontSize: '32px' }}>💰</div>
                <div>
                  <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#00d4ff', margin: 0 }}>{formatCurrency(statsData.totalEarned)}</h3>
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>Total Earned</p>
                </div>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                background: '#0f0f0f',
                borderRadius: '16px'
              }}>
                <div style={{ fontSize: '32px' }}>💳</div>
                <div>
                  <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b', margin: 0 }}>{formatCurrency(userBalance)}</h3>
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>Your Balance</p>
                </div>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                background: '#0f0f0f',
                borderRadius: '16px'
              }}>
                <div style={{ fontSize: '32px' }}>🏆</div>
                <div>
                  <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6', margin: 0 }}>{statsData.awardsGiven}</h3>
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>Awards Received</p>
                </div>
              </div>
            </div>
          </div>

          {/* Refresh Button */}
          <button
            onClick={forceRefreshAll}
            disabled={isRefreshing}
            style={{
              padding: '12px',
              background: '#0f0f0f',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              borderRadius: '16px',
              color: '#00d4ff',
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: isRefreshing ? 0.5 : 1
            }}
          >
            {isRefreshing ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #00d4ff',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                Refreshing...
              </>
            ) : (
              '🔄 Refresh All Data'
            )}
          </button>
        </div>

        {/* Right Column - Dark Theme Tabs */}
        <div>
          <div style={{
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
              flexWrap: 'wrap'
            }}>
              {[
                { id: 'dashboard', label: 'Progress', icon: '📊' },
                { id: 'users', label: 'Referrals', icon: '👥', badge: referredUsers.length },
                { id: 'earnings', label: 'Earnings', icon: '💰' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: activeTab === tab.id ? 'rgba(0, 212, 255, 0.1)' : 'transparent',
                    border: 'none',
                    color: activeTab === tab.id ? '#00d4ff' : '#94a3b8',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: activeTab === tab.id ? '600' : '500',
                    borderBottom: activeTab === tab.id ? '2px solid #00d4ff' : '2px solid transparent'
                  }}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                  {tab.badge > 0 && (
                    <span style={{
                      background: '#00d4ff',
                      color: '#1a1a2e',
                      fontSize: '10px',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontWeight: '600'
                    }}>{tab.badge}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Progress Tab */}
            {activeTab === 'dashboard' && (
              <div style={{ padding: '24px', animation: 'fadeIn 0.3s ease-out' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#00d4ff', marginBottom: '8px' }}>Plan Progress</h3>
                <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '24px' }}>Need 5 referrals per plan to earn the full plan price</p>
                
                {planProgress.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                    <h4 style={{ color: 'white', marginBottom: '8px' }}>No Plan Progress Data</h4>
                    <p style={{ color: '#94a3b8', marginBottom: '20px' }}>Start referring friends to see progress</p>
                    <button onClick={forceRefreshAll} style={{
                      padding: '10px 20px',
                      background: '#00d4ff',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#1a1a2e',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}>Refresh Data</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {planProgress.map((plan) => (
                      <div key={plan.planId} style={{
                        background: '#0f0f0f',
                        borderRadius: '16px',
                        padding: '20px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                          <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#00d4ff', margin: 0 }}>{plan.planName}</h4>
                          <span style={{
                            background: 'rgba(0, 212, 255, 0.2)',
                            color: '#00d4ff',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>Reward: {formatCurrency(plan.awardAmount)}</span>
                        </div>
                        
                        <ProgressBar progress={plan.progress} current={plan.currentReferrals} total={plan.neededReferrals} />
                        
                        <div style={{ margin: '16px 0' }}>
                          {plan.hasEarnedAward ? (
                            <span style={{ color: '#00d4ff', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>🎉</span> Award Earned!
                            </span>
                          ) : (
                            <span style={{ color: '#f59e0b', fontSize: '14px' }}>
                              Need {plan.referralsNeeded} more to earn {formatCurrency(plan.awardAmount)}
                            </span>
                          )}
                        </div>
                        
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                          gap: '12px',
                          paddingTop: '12px',
                          borderTop: '1px solid rgba(0, 212, 255, 0.2)'
                        }}>
                          <div>
                            <span style={{ color: '#94a3b8', fontSize: '12px' }}>Plan Price</span>
                            <p style={{ color: 'white', fontSize: '14px', fontWeight: '600', margin: '4px 0 0 0' }}>{formatCurrency(plan.planPrice)}</p>
                          </div>
                          <div>
                            <span style={{ color: '#94a3b8', fontSize: '12px' }}>Your Potential</span>
                            <p style={{ color: '#00d4ff', fontSize: '14px', fontWeight: '600', margin: '4px 0 0 0' }}>{formatCurrency(plan.awardAmount)}</p>
                          </div>
                          <div>
                            <span style={{ color: '#94a3b8', fontSize: '12px' }}>Current Progress</span>
                            <p style={{ color: 'white', fontSize: '14px', fontWeight: '600', margin: '4px 0 0 0' }}>{plan.currentReferrals}/5 referrals</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Referrals Tab - Dark Theme */}
            {activeTab === 'users' && (
              <div style={{ padding: '24px', animation: 'fadeIn 0.3s ease-out' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#00d4ff', margin: 0 }}>Referred Users</h3>
                  <button onClick={fetchReferredUsers} style={{
                    padding: '8px 16px',
                    background: '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '8px',
                    color: '#00d4ff',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}>🔄 Refresh</button>
                </div>
                
                {referredUsers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
                    <h4 style={{ color: 'white', marginBottom: '8px' }}>No Referrals Yet</h4>
                    <p style={{ color: '#94a3b8', marginBottom: '20px' }}>Share your referral link to start earning</p>
                    <button onClick={handleCopyLink} style={{
                      padding: '12px 24px',
                      background: '#00d4ff',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#1a1a2e',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}>📤 Copy Referral Link</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {referredUsers.map(user => (
                      <div key={user.id} style={{
                        background: '#0f0f0f',
                        borderRadius: '16px',
                        padding: '16px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            background: '#00d4ff',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#1a1a2e',
                            fontWeight: 'bold',
                            fontSize: '18px'
                          }}>
                            {user.username?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div>
                            <div style={{ color: 'white', fontWeight: '500' }}>{user.username || 'Unknown'}</div>
                            <div style={{ color: '#94a3b8', fontSize: '12px' }}>{user.email || 'N/A'}</div>
                          </div>
                        </div>
                        
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '12px',
                          paddingTop: '12px',
                          borderTop: '1px solid rgba(0, 212, 255, 0.2)'
                        }}>
                          <div>
                            <span style={{ color: '#94a3b8', fontSize: '11px' }}>Joined</span>
                            <p style={{ color: 'white', fontSize: '13px', margin: '4px 0 0 0' }}>{formatDate(user.joinDate || user.joinedAt)}</p>
                          </div>
                          <div>
                            <span style={{ color: '#94a3b8', fontSize: '11px' }}>Status</span>
                            <p style={{
                              color: user.contributedToAward ? '#00d4ff' : (user.hasPurchasedPlan ? '#f59e0b' : '#94a3b8'),
                              fontSize: '13px',
                              fontWeight: '500',
                              margin: '4px 0 0 0'
                            }}>
                              {user.contributedToAward ? '✅ Contributed' : (user.hasPurchasedPlan ? '📈 Active' : '📝 Registered')}
                            </p>
                          </div>
                        </div>
                        
                        {user.hasPurchasedPlan && user.purchasedPlan?.name && (
                          <div style={{
                            marginTop: '12px',
                            padding: '8px 12px',
                            background: 'rgba(0, 212, 255, 0.1)',
                            borderRadius: '8px',
                            fontSize: '12px',
                            color: '#00d4ff'
                          }}>
                            💰 {user.purchasedPlan.name} - {formatCurrency(user.purchasedPlan.price || 0)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Earnings Tab - Dark Theme */}
            {activeTab === 'earnings' && (
              <div style={{ padding: '24px', animation: 'fadeIn 0.3s ease-out' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#00d4ff', margin: 0 }}>Earnings History</h3>
                  <button onClick={fetchEarnings} style={{
                    padding: '8px 16px',
                    background: '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '8px',
                    color: '#00d4ff',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}>🔄 Refresh</button>
                </div>
                
                {earnings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>💰</div>
                    <h4 style={{ color: 'white', marginBottom: '8px' }}>No Earnings Yet</h4>
                    <p style={{ color: '#94a3b8' }}>Your referral earnings will appear here</p>
                    <p style={{ color: '#00d4ff', fontSize: '12px', marginTop: '16px' }}>✅ Earn the full plan price when you get 5 referrals for the same plan</p>
                  </div>
                ) : (
                  <>
                    <div style={{
                      background: 'rgba(0, 212, 255, 0.1)',
                      borderRadius: '16px',
                      padding: '16px',
                      marginBottom: '20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                      <span style={{ color: '#94a3b8' }}>Total Awards:</span>
                      <span style={{ color: '#00d4ff', fontWeight: 'bold', fontSize: '18px' }}>{earnings.length}</span>
                      <span style={{ color: '#94a3b8' }}>Total Earned:</span>
                      <span style={{ color: '#00d4ff', fontWeight: 'bold', fontSize: '18px' }}>{formatCurrency(totalEarnings)}</span>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {earnings.map((earning, index) => (
                        <div key={earning.id || index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          padding: '16px',
                          background: '#0f0f0f',
                          borderRadius: '16px',
                          flexWrap: 'wrap'
                        }}>
                          <div style={{ fontSize: '32px' }}>🏆</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '4px' }}>
                              <span style={{ color: 'white', fontWeight: '500' }}>{earning.description || 'Referral Award'}</span>
                              <span style={{ color: '#94a3b8', fontSize: '12px' }}>{earning.formattedDate || formatDate(earning.date)}</span>
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: '13px' }}>Plan: {earning.planName || `Plan ${earning.planId}`}</div>
                          </div>
                          <div style={{ color: '#00d4ff', fontWeight: 'bold', fontSize: '18px' }}>+{formatCurrency(earning.amount || 0)}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* How It Works Card - Dark Theme */}
          <div style={{
            marginTop: '24px',
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            padding: '24px'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#00d4ff', marginBottom: '20px' }}>Referral Program Rules</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>🎯</span>
                <div>
                  <h4 style={{ color: 'white', fontSize: '14px', margin: '0 0 4px 0' }}>5 Referrals Per Plan</h4>
                  <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>Get 5 friends to purchase the same investment plan</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>💰</span>
                <div>
                  <h4 style={{ color: 'white', fontSize: '14px', margin: '0 0 4px 0' }}>Full Plan Price Reward</h4>
                  <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>Earn the entire plan price as cash reward</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>⚡</span>
                <div>
                  <h4 style={{ color: 'white', fontSize: '14px', margin: '0 0 4px 0' }}>Track Multiple Plans</h4>
                  <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>Each of your plans has separate progress tracking</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>📈</span>
                <div>
                  <h4 style={{ color: 'white', fontSize: '14px', margin: '0 0 4px 0' }}>Real-time Updates</h4>
                  <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>Progress updates automatically when referrals purchase</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Dark Theme */}
      <div style={{
        marginTop: '32px',
        padding: '20px',
        background: '#1a1a2e',
        borderRadius: '20px',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        textAlign: 'center'
      }}>
        <p style={{ color: '#94a3b8', marginBottom: '16px' }}>
          💡 <strong>Tip:</strong> Share your referral link to start earning rewards
        </p>
        <button
          onClick={handleCopyLink}
          disabled={statsData.referralLink === 'Not available'}
          style={{
            padding: '12px 24px',
            background: '#00d4ff',
            border: 'none',
            borderRadius: '12px',
            color: '#1a1a2e',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          📤 Share Referral Link
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        body {
          background-color: #0a0a0a;
        }
      `}</style>
    </div>
  );
};

export default ReferralDashboard;